import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { Button } from '../../components/common/Button'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface SellerData {
  store_id: number
  store_name: string
  country: string
  total_orders: number
  total_revenue: number
  avg_order_value: number
  products_count: number
  active_markets: string[]
  performance_score: number
}

export const SellersPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState('revenue')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-sellers', sortBy, sortOrder],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/sellers?sort_by=${sortBy}&sort_order=${sortOrder}`)
      return data
    },
  })

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading seller analytics...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const sellers: SellerData[] = data?.sellers || []
  const summary = data?.summary || {}

  const getPerformanceColor = (score: number) => {
    if (score >= 80) return '#50C878'
    if (score >= 60) return '#4A90E2'
    if (score >= 40) return '#F59E0B'
    return '#EF4444'
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Seller Performance Analysis
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Analyze and compare seller performance across the Expandly platform
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Sellers</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: colors.primary }}>{summary.total_sellers || sellers.length}</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Active Sellers</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#50C878' }}>{summary.active_sellers || 0}</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Revenue</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#7B68EE' }}>
                ${(summary.total_revenue || 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Avg Performance</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#FF6B35' }}>{summary.avg_performance || 0}%</p>
            </div>
          </Card>
        </div>

        {/* Sellers Table */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', color: colors.textPrimary }}>All Sellers</h2>
            <p style={{ color: colors.textSecondary, fontSize: '14px' }}>{sellers.length} sellers found</p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th
                    onClick={() => handleSort('store_name')}
                    style={{
                      textAlign: 'left',
                      padding: '12px',
                      color: colors.textSecondary,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Seller {sortBy === 'store_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Country</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Products</th>
                  <th
                    onClick={() => handleSort('orders')}
                    style={{
                      textAlign: 'right',
                      padding: '12px',
                      color: colors.textSecondary,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Orders {sortBy === 'orders' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('revenue')}
                    style={{
                      textAlign: 'right',
                      padding: '12px',
                      color: colors.textSecondary,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Revenue {sortBy === 'revenue' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>AOV</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Performance</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sellers.map((seller, idx) => (
                  <tr key={seller.store_id} style={{
                    borderBottom: `1px solid ${colors.border}`,
                    backgroundColor: idx % 2 === 0 ? 'transparent' : colors.background,
                  }}>
                    <td style={{ padding: '14px 12px' }}>
                      <div>
                        <p style={{ fontWeight: 500, color: colors.textPrimary }}>{seller.store_name}</p>
                        <p style={{ fontSize: '12px', color: colors.textSecondary }}>ID: {seller.store_id}</p>
                      </div>
                    </td>
                    <td style={{ padding: '14px 12px', color: colors.textPrimary }}>{seller.country}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary, fontWeight: 500 }}>
                      {seller.products_count.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>{seller.total_orders.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${seller.total_revenue.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${seller.avg_order_value.toFixed(2)}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <div style={{
                          width: '60px',
                          height: '8px',
                          backgroundColor: colors.border,
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${seller.performance_score}%`,
                            height: '100%',
                            backgroundColor: getPerformanceColor(seller.performance_score),
                            borderRadius: '4px',
                          }} />
                        </div>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: getPerformanceColor(seller.performance_score),
                        }}>
                          {seller.performance_score}%
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/expansion-pathway/${seller.store_id}`)}
                      >
                        View Pathway
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
